// Eigenbloom's full screen: the interface steps aside and the tunnel fills the screen. Two layers,
// so it works everywhere: an immersive mode (everything but the tunnel hidden — all browsers,
// iPhone included, where Safari has no full-screen API for pages), plus the browser's real full
// screen where it exists (desktop, Android, iPad). A small exit button appears on any tap or
// mouse move; Escape leaves too. On iPhone, a home-screen launch (apple-mobile-web-app-capable)
// also drops Safari's bars.
import { useEffect, useState } from "react";
import { create } from "zustand";

export const useImmersive = create<{ on: boolean }>(() => ({ on: false }));

type FsDoc = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };

const fsElement = () => document.fullscreenElement ?? (document as FsDoc).webkitFullscreenElement ?? null;

/** Enter (call it synchronously in the click handler: browsers need the gesture). */
export function enterImmersive(): void {
  useImmersive.setState({ on: true });
  document.documentElement.dataset.immersive = "1";
  const d = document as FsDoc;
  const el = document.documentElement as FsEl;
  try {
    if (document.fullscreenEnabled && el.requestFullscreen)
      void el.requestFullscreen({ navigationUI: "hide" }).catch(() => undefined);
    else if (d.webkitFullscreenEnabled && el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  } catch {
    // No full-screen API (iPhone): the immersive mode alone.
  }
}

export function exitImmersive(): void {
  if (!useImmersive.getState().on) return;
  useImmersive.setState({ on: false });
  delete document.documentElement.dataset.immersive;
  if (fsElement()) {
    const d = document as FsDoc;
    if (document.exitFullscreen) void document.exitFullscreen().catch(() => undefined);
    else d.webkitExitFullscreen?.();
  }
}

// Leaving the browser's full screen (its own Escape or gesture) leaves the immersive mode too.
if (typeof document !== "undefined")
  for (const ev of ["fullscreenchange", "webkitfullscreenchange"])
    document.addEventListener(ev, () => {
      if (!fsElement() && useImmersive.getState().on && document.fullscreenEnabled) exitImmersive();
    });

/** The exit button while immersed: shown for a few seconds after a tap or mouse move. */
export function ImmersiveExit() {
  const on = useImmersive((s) => s.on);
  const [shown, setShown] = useState(true);
  useEffect(() => {
    if (!on) return;
    let timer = setTimeout(() => setShown(false), 3000);
    const wake = () => {
      setShown(true);
      clearTimeout(timer);
      timer = setTimeout(() => setShown(false), 3000);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && exitImmersive();
    window.addEventListener("pointermove", wake);
    window.addEventListener("pointerdown", wake);
    window.addEventListener("keydown", key);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointermove", wake);
      window.removeEventListener("pointerdown", wake);
      window.removeEventListener("keydown", key);
    };
  }, [on]);
  if (!on) return null;
  return (
    <button
      type="button"
      className={`immersive-exit btn${shown ? "" : " hidden"}`}
      onClick={exitImmersive}
      data-testid="immersive-exit"
      aria-label="Leave full screen"
    >
      ✕ Leave full screen
    </button>
  );
}
